import { cn } from '@neo/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@neo/primitives/sidebar'
import { Check, ChevronsUpDown } from 'lucide-react'
import type { Workspace } from 'v2/shared/navigation/workspace-nav'
import type { ResolvedWorkspace } from 'v2/shared/navigation/workspace-nav-utils'
import { useCloseMobileSidebar } from './workspace-nav-items'

const WorkspaceIcon = ({
  workspace,
  compact,
}: {
  workspace: ResolvedWorkspace
  compact?: boolean
}) => (
  <div
    className={cn(
      'flex shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand-text',
      compact ? 'size-6' : 'size-8',
    )}
  >
    <workspace.icon className="size-4" aria-hidden />
  </div>
)

interface WorkspaceSwitcherProps {
  workspaces: ResolvedWorkspace[]
  current: ResolvedWorkspace
  onSelect: (workspace: Workspace) => void
}

export const WorkspaceSwitcher = ({ workspaces, current, onSelect }: WorkspaceSwitcherProps) => {
  const { state, isMobile } = useSidebar()
  const closeMobile = useCloseMobileSidebar()
  const isCollapsed = state === 'collapsed' && !isMobile

  // Nothing to switch to: no box at all, so the sidebar starts at Home.
  if (workspaces.length < 2) return null

  return (
    <div className="shrink-0 px-3 pb-4 pt-3 md:-mt-px md:pt-0 group-data-[collapsible=icon]:px-2">
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                tooltip={`Switch workspace: ${current.title}`}
                aria-label={`Switch workspace, current workspace: ${current.title}`}
                className="h-12 gap-3 border border-border bg-card px-2 text-primary hover:bg-hv1 data-[state=open]:bg-hv1"
                data-testid="button-workspace-switcher"
              >
                <WorkspaceIcon workspace={current} compact={isCollapsed} />
                {!isCollapsed && (
                  <>
                    <span className="min-w-0 flex-1 truncate text-p-600">{current.title}</span>
                    <ChevronsUpDown className="size-3.5 text-tertiary" aria-hidden />
                  </>
                )}
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side={isCollapsed ? 'right' : 'bottom'}
              align="start"
              sideOffset={8}
              collisionPadding={12}
              className="w-80 max-w-[calc(100vw-2rem)] rounded-lg bg-card p-2"
              aria-label="Switch workspace"
            >
              <DropdownMenuGroup className="space-y-1">
                {workspaces.map((workspace) => (
                  <DropdownMenuItem
                    key={workspace.id}
                    aria-current={workspace.id === current.id}
                    className="gap-3 rounded-md px-3 py-3 aria-[current=true]:bg-brand/5"
                    onSelect={() => {
                      closeMobile()
                      onSelect(workspace.id)
                    }}
                    data-testid={`menuitem-workspace-${workspace.id}`}
                  >
                    <WorkspaceIcon workspace={workspace} />
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <p className="text-s-500">{workspace.title}</p>
                      <p className="text-xs-400 text-secondary">{workspace.description}</p>
                    </div>
                    <span className="size-4 shrink-0" aria-hidden>
                      {workspace.id === current.id && <Check className="size-4 text-brand-text" />}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </div>
  )
}
